import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import { Container, Row, Col, Card } from 'react-bootstrap';
import './NetSuiteTrainingAdvisory.css';
import FAQSection from './FAQSection';

const NetSuiteTrainingAdvisory = () => {
  const controls = useAnimation();
  const [, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    if (heroInView) controls.start("visible");
  }, [controls, heroInView]);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const scaleUp = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6 }
    }
  };

  const whyChooseUs = [
    {
      title: "Streamlined Financial Operations",
      description: "Simplify accounting tasks with NetSuite's unified financial management platform.",
      icon: "bi-gear-fill"
    },
    {
      title: "Automation for Efficiency",
      description: "Save time and reduce manual errors through powerful automation tools.",
      icon: "bi-cpu-fill"
    },
    {
      title: "Informed Decision-Making",
      description: "Gain real-time insights to support smarter financial strategies.",
      icon: "bi-graph-up"
    },
    {
      title: "Improved Cash Flow & Oversight",
      description: "Manage cash, vendors, and suppliers with greater visibility and control.",
      icon: "bi-cash-stack"
    },
    {
      title: "Enterprise-Grade Security",
      description: "Ensure data protection and compliance with robust security measures.",
      icon: "bi-shield-lock"
    }
  ];

  const stats = [
    {
      number: "50+",
      text: "Expert NetSuite Consultants"
    },
    {
      number: "95%",
      text: "Success Rate"
    },
    {
      number: "24/7",
      text: "Available Support"
    },
    {
      number: "100%",
      text: "Guarantee Results"
    }
  ];

  const benefits = [
    {
      title: "Centralized Financial Data",
      description: "NetSuite offers a unified platform for managing all financial data, including general ledger, accounts payable and receivable, cash management, and more. This centralization eliminates data silos, enhancing overall data accuracy",
      icon: "bi-database"
    },
    {
      title: "Automated Financial Processes",
      description: "NetSuite automates various financial processes, such as invoice generation, payment processing, and reconciliation. This automation improves efficiency, reduces errors, and liberates time for more strategic tasks",
      icon: "bi-robot"
    },
    {
      title: "Cash Flow Management",
      description: "NetSuite's cash management features help track and forecast cash flows, providing a clear picture of liquidity and enabling effective cash flow management",
      icon: "bi-graph-up"
    },
    {
      title: "Vendor and Supplier Management",
      description: "NetSuite's vendor management capabilities empower you to oversee vendor information, track payments, and negotiate favorable terms, thereby improving your procurement processes",
      icon: "bi-people"
    },
    {
      title: "Security and Compliance",
      description: "NetSuite prioritizes data security, incorporating features like role-based access controls and compliance certifications to safeguard sensitive financial information",
      icon: "bi-shield-lock"
    },
    {
      title: "Efficiency and Time Savings",
      description: "Through the automation of financial processes, data integration, and the reduction of manual tasks, NetSuite enhances operational efficiency, resulting in significant time savings",
      icon: "bi-stopwatch"
    }
  ];

  const financeFeatures = [
    {
      title: "Streamlined Processes",
      description: "Automate core finance and accounting operations for efficiency.",
      icon: "bi-arrow-repeat"
    },
    {
      title: "Accurate Insights",
      description: "Gain real-time visibility into financial performance.",
      icon: "bi-eye"
    },
    {
      title: "Error Reduction",
      description: "Minimize reporting mistakes and ensure compliance.",
      icon: "bi-check-circle"
    },
    {
      title: "Faster Forecasting",
      description: "Shorten budgeting and forecasting cycles for smarter planning.",
      icon: "bi-speedometer2"
    },
    {
      title: "Improved Cash Flow Management",
      description: "Monitor and manage cash flow more effectively.",
      icon: "bi-cash"
    },
    {
      title: "Regulatory Compliance",
      description: "Stay aligned with financial regulations and standards.",
      icon: "bi-clipboard-check"
    }
  ];

  return (
    <div className="training-advisory-page bg-white overflow-hidden">
      <Helmet>
        <title>NetSuite Financial Management & Training | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/services/financial" />
        <meta name="description" content="Expert NetSuite financial management and training services from SpireNest. Optimize your financial processes with our comprehensive solutions." />
        <meta name="keywords" content="NetSuite financial, financial management, NetSuite training, USA, SpireNest" />
      </Helmet>
      {/* Header */}


      {/* Hero Section */}
      <div className="hero-section" style={{
        minHeight: '92vh',
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      <section style={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        
      }}>
        {/* Floating animated circles */}
        <motion.div 
          animate={{
            y: [0, 20, 0],
            x: [0, 15, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)'
          }}
        />
        
        <motion.div 
          animate={{
            y: [0, -20, 0],
            x: [0, -15, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          style={{
            position: 'absolute',
            bottom: '30%',
            right: '15%',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)'
          }}
        />

        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 1rem', 
          position: 'relative',
          width: '100%'
        }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ 
              textAlign: 'center', 
              padding: '4rem 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <motion.span 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              style={{
                display: 'inline-block',
                color: '#4fc3f7',
                fontWeight: '600',
                letterSpacing: '2px',
                marginBottom: '1rem',
                fontSize: '1.1rem',
                paddingBottom: '1rem'
              }}
            >
              FINANCIAL MANAGEMENT SOLUTION
            </motion.span>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              style={{
                color: 'white',
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                fontWeight: '800',
                lineHeight: '1.2',
                marginBottom: '1.5rem',
                paddingBottom: '1rem',
                textAlign: 'center',
                maxWidth: '800px'
              }}
            >
              Transform Your Financial Operations
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              style={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '1.2rem',
                maxWidth: '600px',
                margin: '0 auto 2.5rem',
                paddingBottom: '1rem',
                textAlign: 'center',
                lineHeight: '1.6'
              }}
            >
              NetSuite provides real-time visibility into financial performance with a unified cloud solution that integrates all your business processes.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
            >
              <button style={{
                background: 'linear-gradient(90deg, #4fc3f7 0%, #1976d2 100%)',
                color: 'white',
                border: 'none',
                padding: '0.8rem 2rem',
                borderRadius: '50px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(31, 136, 255, 0.4)',
                transition: 'transform 0.3s ease'
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              >
                <a href="/contact"  className='btn'>
                    Get Started Today
                </a>
                
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      </div>
      

      {/* Why Choose Us Section - Professional Layout */}
      <section className="why-choose-us-professional py-5 bg-white">
        <Container>
          <motion.div
            ref={servicesRef}
            initial="hidden"
            animate={servicesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <h2 className="fw-bold display-5 mb-3 text-dark">Why Choose Us</h2>
              <p className="lead text-muted">Discover the key advantages that make us your trusted NetSuite partner</p>
            </motion.div>
            
            <Row className="g-4 justify-content-center">
              {whyChooseUs.map((item, index) => (
                <Col lg={6} xl={5} key={index}>
                  <motion.div
                    variants={fadeIn}
                    whileHover={{
                      y: -5,
                      transition: { duration: 0.3, ease: "easeOut" }
                    }}
                    className="why-choose-card"
                  >
                    <Card className="h-100 border-0 shadow-sm bg-white">
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-start">
                          <div className="why-choose-icon-professional me-3">
                            <i className={`${item.icon} text-primary`}></i>
                          </div>
                          <div className="why-choose-content-professional">
                            <h5 className="fw-bold mb-2 text-dark">{item.title}</h5>
                            <p className="text-muted mb-0">{item.description}</p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="stats-section py-5 bg-dark text-white">
        <Container>
          <motion.div
            ref={statsRef}
            initial="hidden"
            animate={statsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="g-4">
              {stats.map((stat, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div variants={scaleUp} className="text-center">
                    <div className="display-3 fw-bold mb-2 text-primary">{stat.number}</div>
                    <p className="mb-0 text-white-50 fs-5">{stat.text}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section - Hexagon Grid Layout */}
      <section className="benefits-section py-5 bg-light position-relative">
        <div className="benefits-pattern"></div>
        <Container className="position-relative">
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <h2 className="fw-bold display-5 mb-3">Benefits of NetSuite Financial Management</h2>
              <p className="lead text-muted">Comprehensive advantages that transform your financial operations</p>
            </motion.div>

            {/* Desktop Hexagon Grid */}
            <div className="benefits-grid d-none d-lg-block">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  className={`benefit-hexagon benefit-${index + 1}`}
                  whileHover={{
                    y: -8,
                    transition: { duration: 0.3, ease: "easeOut" }
                  }}
                >
                  <div className="hexagon-content">
                    <div className="hexagon-icon mb-3">
                      <i className={`${benefit.icon} text-primary`}></i>
                    </div>
                    <h6 className="fw-bold mb-2">{benefit.title}</h6>
                    <p className="text-muted small mb-0">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Alternative Grid for Tablet and Mobile */}
            <Row className="g-4 d-lg-none">
              {benefits.map((benefit, index) => (
                <Col md={6} key={index}>
                  <motion.div
                    variants={fadeIn}
                    whileHover={{
                      y: -5,
                      transition: { duration: 0.3, ease: "easeOut" }
                    }}
                  >
                    <Card className="h-100 border-0 shadow-sm benefit-card-mobile">
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-start">
                          <div className="benefit-icon-mobile me-3">
                            <i className={`${benefit.icon} text-primary fs-2`}></i>
                          </div>
                          <div>
                            <h6 className="fw-bold mb-2">{benefit.title}</h6>
                            <p className="text-muted small mb-0">{benefit.description}</p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Finance Features Section */}
      <section className="finance-features py-5 bg-white  ">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <h2 className="fw-bold display-5 mb-3">Why NetSuite Financial Management</h2>
              <p className="lead text-muted">
                Effective financial management is key to building a long-term vision, guiding investment decisions, and maintaining control over equity and profitability. NetSuite accounting solutions simplify core finance tasks while offering real-time insights.
              </p>
            </motion.div>
            
            <Row className="g-4">
              {financeFeatures.map((feature, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeIn}
                    className="d-flex align-items-start"
                  >
                    <div className="feature-icon me-3 text-primary">
                      <i className={`${feature.icon} fs-2`}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-2">{feature.title}:</h6>
                      <p className="text-muted mb-0 small">{feature.description}</p>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 bg-primary text-white">
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center py-4"
          >
            <h2 className="fw-bold mb-4 display-5">Ready to Optimize Your Financial Management?</h2>
            <p className="lead mb-4">
              Transform your finance operations with NetSuite's comprehensive financial management solutions
            </p>
            <div className="d-flex justify-content-center gap-3">
              <motion.button
                className="btn btn-light btn-lg px-4 py-3 rounded-pill fw-semibold"
                whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(0,0,0,0.2)" }}
                whileTap={{ scale: 0.95 }}
              >
                 <a href="/contact" className='btn'>
                 Get Started Now
                </a>
               
              </motion.button>
              {/* <motion.button
                className="btn btn-outline-light btn-lg px-4 py-3 rounded-pill fw-semibold"
                whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
              </motion.button> */}
            </div>
          </motion.div>
        </Container>
      </section>
      <FAQSection />
    </div>
  );
};

export default NetSuiteTrainingAdvisory;